import { useEffect, useState } from 'react';
import { BrandLockup } from '../components/BrandLockup.js';
import type { IdleDisplay } from '../lib/idle-display.js';

type WaitingForContentScreenProps = {
  visible: boolean;
  idleDisplay?: IdleDisplay;
};

function useClockLabel(enabled: boolean): string {
  const [label, setLabel] = useState(() => new Date().toLocaleTimeString());

  useEffect(() => {
    if (!enabled) return;
    const tick = () => setLabel(new Date().toLocaleTimeString());
    tick();
    const timer = window.setInterval(tick, 1_000);
    return () => window.clearInterval(timer);
  }, [enabled]);

  return label;
}

/**
 * Shown while paired and syncing, before any media has painted on stage.
 * Mode comes from organisation Settings (`idleDisplay`).
 */
export function WaitingForContentScreen({
  visible,
  idleDisplay = 'logo',
}: WaitingForContentScreenProps) {
  const clock = useClockLabel(visible && idleDisplay === 'clock');
  if (!visible) return null;

  if (idleDisplay === 'black') {
    return <div className="waiting-screen waiting-screen--black" aria-hidden />;
  }

  if (idleDisplay === 'clock') {
    return (
      <div className="waiting-screen waiting-screen--clock" role="status" aria-live="polite">
        <p className="waiting-screen__clock">{clock}</p>
      </div>
    );
  }

  return (
    <div className="waiting-screen" role="status" aria-live="polite">
      <div className="waiting-screen__panel">
        <BrandLockup className="waiting-screen__brand" />
        <p className="waiting-screen__title">Paired — waiting for content</p>
        <p className="waiting-screen__lede">
          The player is downloading the published playlist. Large videos can take a minute. In the
          dashboard: confirm the playlist is assigned and <strong>Published</strong>, then use{' '}
          <strong>Sync now</strong> on this display.
        </p>
      </div>
    </div>
  );
}
